import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Container, Paper } from '@mui/material';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { atcb_action } from 'add-to-calendar-button-react';

const getRecurrenceText = (type) => {
  switch (type) {
    case 1: return 'DAILY';
    case 2: return 'WEEKLY';
    case 3: return 'MONTHLY';
    default: return null;
  }
};

const EventPage = () => {
  const { id } = useParams();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isMac = /Mac/i.test(navigator.userAgent) || /Mac/i.test(navigator.platform);
  const isVisionPro = !isIOS && /visionOS|xrOS|Vision/i.test(navigator.userAgent);

  const eventName = eventData?.name || eventData?.title;
  const title = `${eventName || 'AuraTap Event'} | AuraTap Event`;

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        // Use relative URL to leverage existing proxy/Nginx setup
        const response = await axios.get(`/api/Event/getEventShareInfo?shareCode=${id}`);
        setEventData(response.data);
      } catch (err) {
        console.error('Error fetching event data:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  const handleJoin = (e) => {
    if (isVisionPro) {
      // Attempt to open the app via TestFlight or Universal Link fallback
      window.location.href = "https://apps.apple.com/app/6754709124";
    } else {
      const formatDate = (dateString) => {
        const d = new Date(dateString);
        const pad = (n) => String(n).padStart(2, '0');
        return {
          date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
          time: `${pad(d.getHours())}:${pad(d.getMinutes())}`
        };
      };

      const start = formatDate(eventData.startDateUtc);
      const end = formatDate(eventData.endDateUtc);

      atcb_action({
        name: title,
        description: eventData.description || '',
        startDate: start.date,
        startTime: start.time,
        endDate: end.date,
        endTime: end.time,
        location: window.location.href,
        options: ['Apple'],
        timeZone: "currentBrowser"
      }, e.currentTarget);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#000000',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        color: '#fff',
        fontFamily: 'Unbounded, sans-serif'
      }}
    >
      <Header />

      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        {loading ? (
          <CircularProgress sx={{ color: '#30D158' }} />
        ) : error || !eventData ? (
          <Typography variant="h5" color="error" fontFamily="Unbounded, sans-serif">
            Event not found
          </Typography>
        ) : (
          <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Event Image */}
            {eventData.imageUrl && !imageError && (
              <Box
                component="img"
                src={eventData.imageUrl}
                alt="Event Banner"
                onError={() => setImageError(true)}
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.6)',
                  mb: 4,
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              />
            )}

            {/* Event Time */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              {eventData.eventState === 2 ? (
                <Typography variant="h5" color="error" fontFamily="Unbounded, sans-serif" sx={{ mb: eventData.recurrenceType > 0 ? 1 : 0, textAlign: 'center' }}>
                  Event has ended
                </Typography>
              ) : (
                <Typography
                  variant="h6"
                  sx={{
                    color: '#30D158',
                    fontFamily: 'Unbounded, sans-serif',
                    fontWeight: 600,
                    mb: eventData.recurrenceType > 0 ? 1 : 0,
                    textAlign: 'center'
                  }}
                >
                  {new Date(eventData.startDateUtc).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  {' - '}
                  {new Date(eventData.endDateUtc).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                </Typography>
              )}
              {eventData.recurrenceType > 0 && (
                <Box
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'rgba(255, 255, 255, 0.8)',
                    letterSpacing: '1px',
                    fontFamily: 'Unbounded, sans-serif'
                  }}
                >
                  {getRecurrenceText(eventData.recurrenceType)}
                </Box>
              )}
            </Box>

            {/* Event Description */}
            {eventData.description && (
              <Typography
                variant="body1"
                sx={{
                  mb: 5,
                  color: 'rgba(255,255,255,0.8)',
                  whiteSpace: 'pre-line',
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  fontFamily: 'Unbounded, sans-serif'
                }}
              >
                {eventData.description}
              </Typography>
            )}
            {/* Disclaimer for non-Vision Pro users */}
            {eventData.eventState !== 2 && !isVisionPro && (
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  color: 'rgba(255,255,255,0.6)',
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  fontFamily: 'Unbounded, sans-serif'
                }}
              >
                In order to join the event you will have to open the link on your Vision Pro
              </Typography>
            )}

            {eventData.eventState !== 2 && (
              <Button
                variant="contained"
                onClick={handleJoin}
                fullWidth
                sx={{
                  backgroundColor: '#30D158',
                  color: '#FFFFFF',
                  fontWeight: '700',
                  py: 2,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontSize: '1.2rem',
                  fontFamily: 'Unbounded, sans-serif',
                  transition: 'transform 0.3s ease, background-color 0.3s ease',
                  boxShadow: '0 8px 24px rgba(48, 209, 88, 0.3)',
                  '&:hover': {
                    backgroundColor: '#30D158',
                    boxShadow: '0 12px 32px rgba(48, 209, 88, 0.5)',
                    transform: 'scale(1.03)',
                  }
                }}
              >
                {isVisionPro ? 'Join Event' : 'Add to Calendar'}
              </Button>
            )}
          </Container>
        )}
      </Box>

      <Footer />
    </Box>
  );
};

export default EventPage;
